import React, { useRef, useState } from "react";
import { ChevronDown, FileUp, X, CheckCircle2 } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalTitle,
} from "../../../shared/ui";

export type TransactionStatusOption = "Processing" | "Completed" | "Failed";

export interface UpdateStatusSubmitData {
  status: TransactionStatusOption;
  reason?: string;
  receiptFile?: File | null;
}

interface UpdateStatusModalProps {
  open: boolean;
  currentStatus?: string;
  onClose: () => void;
  onConfirm: (data: UpdateStatusSubmitData) => void | Promise<void>;
  isSubmitting?: boolean;
}

const STATUS_OPTIONS: TransactionStatusOption[] = [
  "Processing",
  "Completed",
  "Failed",
];

export const UpdateStatusModal: React.FC<UpdateStatusModalProps> = ({
  open,
  onClose,
  onConfirm,
  isSubmitting = false,
}) => {
  const [selectedStatus, setSelectedStatus] =
    useState<TransactionStatusOption>("Completed");
  const [reason, setReason] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
    setReason("");
    setReceiptFile(null);
  };

  const isFormValid =
    selectedStatus === "Failed" ? reason.trim().length > 0 : true;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File size exceeds 5 MB limit.");
        return;
      }
      setReceiptFile(file);
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setReceiptFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || isSubmitting) return;

    onConfirm({
      status: selectedStatus,
      reason: selectedStatus === "Failed" ? reason.trim() : undefined,
      receiptFile: selectedStatus === "Completed" ? receiptFile : null,
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[480px] bg-white rounded-[20px] p-6"
    >
      <ModalHeader className="flex px-0 pt-0 items-center justify-between pb-4">
        <ModalTitle className="text-[18px] md:text-[20px] font-semibold text-neutral-900">
          Update transaction status
        </ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          disabled={isSubmitting}
          onClick={handleClose}
          className="rounded-full p-1 text-neutral-900 hover:bg-neutral-100 transition-colors disabled:opacity-50"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5 pt-2">
        <ModalBody className="flex flex-col gap-5 p-0">
          {/* Status Dropdown */}
          <div className="flex flex-col gap-2">
            <p className="text-[14px] font-medium text-neutral-700">
              Status
            </p>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="flex h-[46px] w-full items-center justify-between rounded-[8px] bg-surface-input px-4 text-left transition-colors hover:bg-surface-inputHover focus:outline-none focus:ring-2 focus:ring-brand-ring"
                >
                  <span className="text-[14px] font-medium text-gray-30">
                    {selectedStatus}
                  </span>
                  <ChevronDown size={18} className="text-gray-70 shrink-0" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="start"
                  sideOffset={4}
                  className="z-[150] w-[var(--radix-dropdown-menu-trigger-width)] rounded-[12px] border border-neutral-200 bg-white p-1.5 shadow-lg"
                >
                  {STATUS_OPTIONS.map((opt) => {
                    const isSelected = selectedStatus === opt;
                    return (
                      <DropdownMenu.Item
                        key={opt}
                        onSelect={() => setSelectedStatus(opt)}
                        className={`flex cursor-pointer items-center justify-between rounded-[8px] px-3.5 py-2.5 text-[14px] outline-none transition-colors hover:bg-neutral-50 focus:bg-neutral-50 ${
                          isSelected
                            ? "font-medium text-neutral-900 bg-neutral-50"
                            : "text-neutral-700"
                        }`}
                      >
                        <span>{opt}</span>
                        {isSelected && (
                          <CheckCircle2 size={16} className="text-brand" />
                        )}
                      </DropdownMenu.Item>
                    );
                  })}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>

          {/* Conditional View: Completed -> Upload transaction receipt */}
          {selectedStatus === "Completed" && (
            <div className="flex flex-col gap-2">
              <p className="text-[14px] font-medium text-neutral-700">
                Upload transaction receipt
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpeg,.jpg,.png,.pdf,image/jpeg,image/png,application/pdf"
                onChange={handleFileChange}
                className="hidden"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                className="flex cursor-pointer items-center gap-3.5 rounded-[12px] border border-dashed border-brand-border bg-brand-light p-4 transition-colors hover:bg-primary-90"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] bg-brand-badge">
                  <FileUp size={20} className="text-brand-icon" />
                </div>
                <div className="flex flex-1 flex-col overflow-hidden">
                  <span className="truncate text-[14px] font-medium text-neutral-900">
                    {receiptFile ? receiptFile.name : "Tap to upload receipt"}
                  </span>
                  <span className="text-[12px] text-neutral-500">
                    {receiptFile
                      ? `${(receiptFile.size / (1024 * 1024)).toFixed(2)} MB`
                      : "JPEG, PNG, JPG, PDF Max. file size: 5.0 MB 5 MB max."}
                  </span>
                </div>
                {receiptFile && (
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="rounded-full p-1 text-neutral-500 hover:bg-white/80 hover:text-danger-700 transition-colors"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Conditional View: Failed -> What's wrong with this transaction? */}
          {selectedStatus === "Failed" && (
            <div className="flex flex-col gap-1.5">
              <p className="text-[14px] font-medium text-neutral-700">
                What's wrong with this transaction?
              </p>
              <textarea
                rows={3}
                maxLength={100}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter a reason"
                className="min-h-[100px] w-full resize-none rounded-[8px] border border-transparent bg-surface-input p-3.5 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus:border-brand focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-ring transition-all"
              />
              <div className="flex justify-end pt-0.5">
                <span className="text-[12px] text-gray-70">
                  {reason.length}/100
                </span>
              </div>
            </div>
          )}
        </ModalBody>

        {/* Footer / Submit Button */}
        <div className="flex justify-end pt-2">
          <Button
            type="submit"
            disabled={isSubmitting || !isFormValid}
            className="px-6"
          >
            {isSubmitting ? "Updating..." : "Update status"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
